import React, { useState, useEffect } from 'react';
import { message, Table, Input } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';

interface TaraFardo {
  id: number;
  id_producto: number;
  producto: string;
  descripcion_producto: string;
  tara: number;
  denominacion: string;
  localidad: string;
  provincia: string;
  direccion: string;
}

interface Sucursal {
  id: number;
  denominacion: string;
  direccion: string;
  localidad: string;
  provincia: string;
}

const TaraFardos: React.FC = () => {
  const { getAccessTokenSilently, user } = useAuth0();
  const id_empresa = user?.id_empresa;
  const id_sucursal = user?.id_sucursal;
  const [isLoading, setIsLoading] = useState(true);
  const [tarasFardos, setTarasFardos] = useState<TaraFardo[]>([]);
  const [editingKey, setEditingKey] = useState<number | null>(null);
  const [editingValue, setEditingValue] = useState<string>('');
  const [sucursal, setSucursal] = useState<Sucursal | null>(null);

  console.log('user',user)
  console.log('id_empresa',id_empresa)
  console.log('id_sucursal',id_sucursal)
  const columns = [
    {
      title: 'Descripción',
      dataIndex: 'descripcion_producto',
      key: 'descripcion_producto',
      width: '20%'
    },
    {
      title: 'Tara (Kg/Fardo)',
      dataIndex: 'tara',
      key: 'tara',
      width: '10%',
      align: 'right' as const,
      render: (value: number, record: TaraFardo) => {
        if (editingKey === record.id) {
          return (
            <Input
              type="text"
              value={editingValue}
              onChange={(e) => {
                const value = e.target.value;
                if (value === '' || value === '0' || value === '0.' || /^\d*\.?\d{0,2}$/.test(value)) {
                  setEditingValue(value);
                }
              }}
              onKeyPress={(e) => {
                if (!/[\d.]/.test(e.key) && e.key !== 'Enter' && e.key !== 'Backspace') {
                  e.preventDefault();
                }
                if (e.key === '.' && editingValue.includes('.')) {
                  e.preventDefault();
                }
              }}
              onPressEnter={() => handleSave(record.id)}
              onBlur={() => handleSave(record.id)}
              autoFocus
              style={{ 
                width: '80px',
                textAlign: 'right'
              }}
            />
          );
        }
        return (
          <div 
            style={{ cursor: 'pointer' }}
            onClick={() => {
              setEditingKey(record.id);
              setEditingValue(value.toString());
            }}
          >
            {(value || 0).toFixed(2)}
          </div>
        );
      }
    }
  ];

  const handleSave = async (id: number) => {
    try {
      const tara = tarasFardos.find(t => t.id === id);
      if (!tara) return;

      const taraValue = parseFloat(editingValue);
      
      if (isNaN(taraValue)) {
        message.error('Por favor ingrese un valor numérico válido');
        return;
      }

      console.log('Enviando valor actualizado:', {
        id_producto: tara.id_producto,
        tara: taraValue
      });

      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_RELACION_URL}/actualizar_tara_fardo/${id_empresa}/${id_sucursal}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            id_producto: tara.id_producto,
            tara: taraValue
          })
        }
      );

      const data = await response.json();
      
      if (data.status === 'success') {
        const newTaras = tarasFardos.map(t => {
          if (t.id === id) {
            return { ...t, tara: taraValue };
          }
          return t;
        });
        setTarasFardos(newTaras);
        
        message.success('Tara actualizada correctamente');
        setEditingValue('');
        setEditingKey(null);
        
        await cargarTarasFardos();
      } else {
        throw new Error(data.message || 'Error al actualizar tara');
      }

    } catch (error) {
      console.error('Error:', error);
      message.error('Error al actualizar la tara');
      await cargarTarasFardos();
    } finally {
      setEditingKey(null);
      setEditingValue('');
    }
  };

  const cargarTarasFardos = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_RELACION_URL}/listar_tara_fardo/${id_empresa}/${id_sucursal}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al cargar taras de fardos');
      }

      const data = await response.json();
      setTarasFardos(data);
    } catch (error) {
      console.error('Error al cargar:', error);
      message.error('Error al cargar las taras de fardos');
    }
  };

  useEffect(() => {
    inicializarYCargarDatos();
  }, []);

  const inicializarYCargarDatos = async () => {
    try {
      if (!id_empresa || !id_sucursal) {
        message.error('No se pudo obtener información de la empresa');
        return;
      }

      setIsLoading(true);
      await cargarSucursal();
      
      const token = await getAccessTokenSilently();

      // Inicializar taras
      const initResponse = await fetch(
        `${process.env.REACT_APP_RELACION_URL}/crear_relacion_tara_fardo/${id_empresa}/${id_sucursal}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!initResponse.ok) {
        throw new Error('Error al inicializar taras de fardos');
      }

      // Cargar taras
      await cargarTarasFardos();

    } catch (error) {
      console.error('Error:', error);
      message.error('Error al inicializar datos');
    } finally {
      setIsLoading(false);
    }
  };

  const cargarSucursal = async () => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_SUCURSAL_URL}/listar_sucursal/${id_empresa}/${id_sucursal}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Error al cargar datos de sucursal');
      }

      const data = await response.json();
      setSucursal(data);
      console.log('sucursal',data)
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar datos de la sucursal');
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <h2 style={{ 
        textAlign: 'center', 
        margin: '20px 0',
        backgroundColor: '#1890ff',
        color: 'white',
        padding: '10px'
      }}>
        TARAS FARDOS
      </h2>

      {/* Información de Sucursal */}
      {!isLoading && (
        <div style={{ 
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '20px'
        }}>
          <table style={{ 
            width: '30%',
            border: '1px solid #d9d9d9',
            borderCollapse: 'collapse',
            marginBottom: '20px'
          }}>
            <tbody>
              <tr>
                <td style={{ 
                  padding: '8px',
                  border: '1px solid #d9d9d9',
                  backgroundColor: '#fafafa',
                  fontWeight: 'bold',
                  width: '30%'
                }}>
                  Sucursal:
                </td>
                <td style={{ 
                  padding: '8px',
                  border: '1px solid #d9d9d9'
                }}>
                  {sucursal?.denominacion || ''}
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '8px',
                  border: '1px solid #d9d9d9',
                  backgroundColor: '#fafafa',
                  fontWeight: 'bold'
                }}>
                  Dirección:
                </td>
                <td style={{ 
                  padding: '8px',
                  border: '1px solid #d9d9d9'
                }}>
                  {sucursal?.direccion || ''}
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '8px',
                  border: '1px solid #d9d9d9',
                  backgroundColor: '#fafafa',
                  fontWeight: 'bold'
                }}>
                  Localidad:
                </td>
                <td style={{ 
                  padding: '8px',
                  border: '1px solid #d9d9d9'
                }}>
                  {sucursal?.localidad || ''}
                </td>
              </tr>
              <tr>
                <td style={{ 
                  padding: '8px',
                  border: '1px solid #d9d9d9',
                  backgroundColor: '#fafafa',
                  fontWeight: 'bold'
                }}>
                  Provincia:
                </td>
                <td style={{ 
                  padding: '8px',
                  border: '1px solid #d9d9d9'
                }}>
                  {sucursal?.provincia || ''}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
      
      {/* Tabla de Taras */}
      <div style={{ 
        display: 'flex',
        justifyContent: 'center',
        width: '100%'
      }}>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            Cargando taras de fardos...
          </div>
        ) : (
          <Table
            scroll={{ x: 'max-content' }}
            dataSource={tarasFardos}
            columns={columns}
            rowKey="id"
            pagination={false}
            bordered
            size="middle"
            style={{ 
              marginTop: '20px',
              width: '30%',
              border: '2px solid #d9d9d9'
            }}
            onRow={(record) => ({
              onClick: () => {
                setEditingKey(record.id);
                setEditingValue(record.tara.toString());
              }
            })}
          />
        )}
      </div>
    </div>
  );
};

export default TaraFardos;
